import { forwardRef } from "react";

import { cn } from "@oneleet/utils";

import { type VariantProps, cva } from "class-variance-authority";

const DEFAULT_VARIANT = "outlined";
const DEFAULT_COLOR = "neutral";
const DEFAULT_SIZE = "base";

const badge = cva(
  "font-ng-medium flex h-min w-min select-none flex-row items-center justify-center gap-1 whitespace-nowrap rounded-full",
  {
    variants: {
      color: {
        accent: "text-accent-950",
        neutral: "text-neutral-950",
        green: "text-ng-green-950",
        red: "text-ng-red-950",
        orange: "text-ng-orange-950",
        yellow: "text-ng-yellow-950",
      },
      size: {
        xs: "text-ng-xs px-1.5 py-0.5",
        sm: "text-ng-sm px-2 py-0.5",
        base: "text-ng-md px-2.5 py-0.5",
      },
      variant: {
        solid: "text-white",
        outlined: "ring-1 ring-inset",
        subtle: "",
      },
    },
    compoundVariants: [
      // Outlined variants
      {
        variant: "outlined",
        color: "accent",
        className: "bg-accent-100 ring-accent-600",
      },
      {
        variant: "outlined",
        color: "neutral",
        className: "bg-white ring-neutral-300",
      },
      {
        variant: "outlined",
        color: "green",
        className: "bg-ng-green-100 ring-ng-green-600",
      },
      {
        variant: "outlined",
        color: "red",
        className: "bg-ng-red-200 ring-ng-red-600",
      },
      {
        variant: "outlined",
        color: "orange",
        className: "bg-ng-orange-100 ring-ng-orange-600",
      },
      {
        variant: "outlined",
        color: "yellow",
        className: "bg-ng-yellow-100 ring-ng-yellow-600",
      },

      // Subtle variants
      {
        variant: "subtle",
        color: "accent",
        className: "bg-accent-300",
      },
      {
        variant: "subtle",
        color: "neutral",
        className: "bg-neutral-150",
      },
      {
        variant: "subtle",
        color: "green",
        className: "bg-ng-green-300",
      },
      {
        variant: "subtle",
        color: "red",
        className: "bg-ng-red-300",
      },
      {
        variant: "subtle",
        color: "orange",
        className: "bg-ng-orange-300",
      },
      {
        variant: "subtle",
        color: "yellow",
        className: "bg-ng-yellow-300",
      },

      // Solid variants
      {
        variant: "solid",
        color: "accent",
        className: "bg-accent-800",
      },
      {
        variant: "solid",
        color: "neutral",
        className: "bg-neutral-800",
      },
      {
        variant: "solid",
        color: "green",
        className: "bg-ng-green-800",
      },
      {
        variant: "solid",
        color: "red",
        className: "bg-ng-red-800",
      },
      {
        variant: "solid",
        color: "orange",
        className: "bg-ng-orange-800",
      },
      {
        variant: "solid",
        color: "yellow",
        className: "bg-ng-yellow-800",
      },
    ],
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      color: DEFAULT_COLOR,
      size: DEFAULT_SIZE,
    },
  },
);

const iconClassNames = cva("flex items-center justify-center", {
  variants: {
    color: {
      accent: "text-accent-900",
      neutral: "text-neutral-900",
      green: "text-ng-green-900",
      red: "text-ng-red-900",
      orange: "text-ng-orange-900",
      yellow: "text-ng-yellow-900",
    },
    size: {
      xs: "size-2.5",
      sm: "size-3",
      base: "size-4",
    },
    variant: {
      solid: "text-white",
      outlined: "",
      subtle: "",
    },
  },
});

export type NgBadgeVariantProps = VariantProps<typeof badge>;

type NgBadgeProps = NgBadgeVariantProps &
  React.BaseHTMLAttributes<HTMLDivElement> & {
    icon?: React.ReactNode;
  };

export const NgBadge = forwardRef<HTMLDivElement, NgBadgeProps>(
  (
    {
      variant = DEFAULT_VARIANT,
      color = DEFAULT_COLOR,
      size = DEFAULT_SIZE,
      icon,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          badge({
            variant,
            color,
            size,
          }),
          className,
        )}
        {...rest}
      >
        {icon && (
          <div className={cn(iconClassNames({ size, variant, color }))}>
            {icon}
          </div>
        )}
        {children}
      </div>
    );
  },
);

NgBadge.displayName = "NgBadge";
